<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import { cn } from '@/lib/utils'
import type {
  ColDef,
  GridOptions,
  RowClickedEvent,
  CellClickedEvent,
  SelectionChangedEvent,
  CellValueChangedEvent,
  RowDoubleClickedEvent,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community'
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community'
// Enterprise 모듈 import (Grouping, Pivot, Excel Export 등)
import { AllEnterpriseModule } from 'ag-grid-enterprise'

// AG Grid 모듈 등록 (Community + Enterprise)
ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule])

// ── 공식 권장: data-ag-theme-mode attribute 기반 다크모드 전환 ─────────────────
// AG Grid v33 공식 패턴:
//   1. themeQuartz.withParams({...}, 'light').withParams({...}, 'dark') 로 두 모드 정의
//   2. data-ag-theme-mode attribute 전환으로 모드 스위칭 (theme 객체 재생성 불필요)
//   3. CSS --ag-* 변수 오버라이드로 앱 CSS 변수 참조 (getCSSVar 우회 불필요)
const jTheme = themeQuartz
  .withParams({
    // 공통 사이즈 (라이트/다크 모두 적용)
    cellHorizontalPaddingScale: 1,
    columnBorder: true,
    fontSize: 13,
    headerFontWeight: 600,
    headerVerticalPaddingScale: 0.45,
    iconSize: 14,
    rowVerticalPaddingScale: 0.4,
  })
  .withParams({
    browserColorScheme: 'light',
  }, 'light')
  .withParams({
    browserColorScheme: 'dark',
  }, 'dark')

// JLIS는 .dark class 기반 다크모드이므로 MutationObserver로 감지 후 attribute 동기화
const isDark = ref(typeof document !== 'undefined' && document.documentElement.classList.contains('dark'))
const gridContainerRef = ref<HTMLElement | null>(null)
let darkObserver: MutationObserver | null = null

// isDark 변경 → data-ag-theme-mode 동기화 (AG Grid가 내부적으로 반응)
watch(isDark, (dark) => {
  gridContainerRef.value?.setAttribute('data-ag-theme-mode', dark ? 'dark' : 'light')
})

onMounted(() => {
  // 초기 attribute 설정
  gridContainerRef.value?.setAttribute('data-ag-theme-mode', isDark.value ? 'dark' : 'light')

  // 이후 변경 감지
  darkObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})

onUnmounted(() => {
  darkObserver?.disconnect()
})

/**
 * Action Button 정의 타입
 */
export type ActionButton = {
  /** 버튼 아이콘 이름 (lucide) - label이 없을 때 기본 텍스트 생성에 사용 */
  icon?: string
  /** 버튼 라벨 */
  label?: string
  /** 툴팁 텍스트 */
  tooltip?: string
  /** 버튼 클릭 핸들러 (rowData 전달) */
  onClick: (rowData: any) => void
  /** 버튼 스타일 타입 */
  styletype?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
  /** 조건부 표시 함수 (rowData를 받아 boolean 반환) */
  show?: (rowData: any) => boolean
}

const props = withDefaults(
  defineProps<{
    /** 추가 클래스 (외부 커스터마이징용) */
    class?: string
    /** 그리드에 표시할 데이터 배열 */
    rowData: any[]
    /** 컬럼 정의 배열 */
    columnDefs: ColDef[]
    /** 페이지네이션 활성화 여부 */
    pagination?: boolean
    /** 체크박스 선택 활성화 여부 */
    checkbox?: boolean
    /** 행 번호 표시 여부 (Enterprise) */
    rowNumbers?: boolean
    /** 플로팅 필터 표시 여부 */
    floatingFilters?: boolean
    /** 플로팅 필터 표시 여부 (하위호환 alias) */
    floatingFilter?: boolean
    /** 행 번호 컬럼 너비(px) */
    rowNumberWidth?: number
    /** 행별 액션 버튼 목록 */
    actionButtons?: ActionButton[]
    /** 요약 컬럼 표시 여부 */
    summaryColumn?: boolean
    /** 숨김 컬럼 관리 활성화 여부 */
    hiddenColumn?: boolean
    /** 그룹핑 기능 활성화 여부 (Enterprise) */
    enableGrouping?: boolean
    /** 피벗 기능 활성화 여부 (Enterprise) */
    enablePivot?: boolean
    /** Excel 내보내기 기능 활성화 여부 (Enterprise) */
    enableExcelExport?: boolean
    /** Tree Data 기능 활성화 여부 (Enterprise) */
    enableTreeData?: boolean
    /** Tree Data 계층 경로를 반환하는 함수 */
    getDataPath?: (data: any) => (string | number)[]
    /** Tree Data 그룹 컬럼 정의 */
    autoGroupColumnDef?: ColDef
    /** 선택된 행 데이터 배열 (v-model:selected-rows) */
    selectedRows?: any[]
    /** 컬럼 호버 하이라이트 활성화 여부 */
    columnHover?: boolean
    /** Columns Tool Panel 활성화 여부 (Enterprise) */
    enableColumnsToolPanel?: boolean
    /** Status Bar 활성화 여부 (Enterprise) */
    statusBar?: boolean
    /** 간소화된 Footer 모드 (Status Bar 제거, Pagination만 표시) */
    compactFooter?: boolean
    /** Row Group Panel 표시 여부 ('always' | 'onlyWhenGrouping' | 'never') */
    rowGroupPanelShow?: 'always' | 'onlyWhenGrouping' | 'never'
    /** 그룹 기본 확장 레벨 (-1: 모두 닫힘, 0: 첫 레벨만, 1: 2레벨까지...) */
    groupDefaultExpanded?: number
    /** Pivot Mode Panel 표시 여부 */
    pivotPanelShow?: 'always' | 'onlyWhenPivoting' | 'never'
    /** Pivot Mode 활성화 여부 */
    pivotMode?: boolean
    /** Row Drag 관리 모드 (AG Grid 내장 정렬 사용). true 시 pagination 은 강제로 false */
    rowDragManaged?: boolean
    /** Row 전체를 드래그 핸들로 사용 (행 어느 곳이든 드래그 시작 가능) */
    rowDragEntireRow?: boolean
    /** 다중 행 드래그 허용 (rowSelection='multiRow' 필요) */
    rowDragMultiRow?: boolean
    /** rowData 내부 필드 변경(깊은 변이)에 반응할지 여부. 기본 true(기존 동작). */
    /** managed drag 사용 시 false 로 두어 내부 mutation 으로 인한 전체 재주입(드래그 롤백) 방지 권장. */
    /** 주의: watcher 의 deep 옵션은 컴포넌트 마운트 시점에 고정됩니다. */
    reactiveRowData?: boolean
  }>(),
  {
    pagination: true,
    checkbox: false,
    rowNumbers: true,
    floatingFilters: true,
    floatingFilter: undefined,
    rowNumberWidth: 38,
    actionButtons: undefined,
    summaryColumn: false,
    hiddenColumn: false,
    enableGrouping: true,
    enablePivot: false,
    enableExcelExport: false,
    enableTreeData: false,
    getDataPath: undefined,
    autoGroupColumnDef: undefined,
    selectedRows: () => [],
    columnHover: true,
    enableColumnsToolPanel: true,
    statusBar: true,
    compactFooter: false,
    rowGroupPanelShow: 'never',
    groupDefaultExpanded: -1,
    pivotPanelShow: 'never',
    pivotMode: false,
    rowDragManaged: false,
    rowDragEntireRow: false,
    rowDragMultiRow: false,
    reactiveRowData: true,
  },
)

const emit = defineEmits<{
  /** 행 클릭 이벤트 */
  rowClicked: [event: RowClickedEvent]
  /** 행 더블클릭 이벤트 */
  rowDoubleClicked: [event: RowDoubleClickedEvent]
  /** 셀 클릭 이벤트 */
  cellClicked: [event: CellClickedEvent]
  /** 선택 변경 이벤트 (체크박스 등) */
  selectionChanged: [event: SelectionChangedEvent]
  /** 셀 값 변경 이벤트 */
  cellValueChanged: [event: CellValueChangedEvent]
  /** 그리드 준비 완료 이벤트 */
  gridReady: [event: GridReadyEvent]
  /** 선택된 행 변경 이벤트 (v-model:selected-rows) */
  'update:selectedRows': [rows: any[]]
}>()

// ag-Grid 인스턴스 참조
const gridApi = ref<any>(null)
const gridColumnApi = ref<any>(null)

// Action Buttons Cell Renderer - 함수형으로 DOM 직접 생성
const ActionButtonsCellRenderer = (params: ICellRendererParams) => {
  const buttons = props.actionButtons || []
  const rowData = params.data
  
  // 표시할 버튼 필터링
  const visibleButtons = buttons.filter(btn => {
    if (btn.show) {
      return btn.show(rowData)
    }
    return true
  })
  
  if (visibleButtons.length === 0) {
    return ''
  }
  
  // 컨테이너 div 생성
  const container = document.createElement('div')
  container.className = 'flex items-center gap-1'
  
  // 각 버튼 생성
  visibleButtons.forEach((btn) => {
    const button = document.createElement('button')
    button.className = btn.styletype === 'danger' ? 'j-action-btn j-action-btn-danger' : 'j-action-btn'
    
    // tooltip
    if (btn.tooltip) {
      button.title = btn.tooltip
    }
    
    // 라벨 추가 (텍스트 버튼)
    if (btn.label) {
      button.textContent = btn.label
    } else if (btn.icon) {
      // 라벨이 없으면 기본 텍스트 생성
      if (btn.icon === 'pencil') {
        button.textContent = '수정'
      } else if (btn.icon === 'trash2' || btn.icon === 'trash') {
        button.textContent = '삭제'
      } else if (btn.icon === 'eye') {
        button.textContent = '보기'
      } else if (btn.icon === 'copy') {
        button.textContent = '복사'
      } else if (btn.icon === 'download') {
        button.textContent = '다운로드'
      } else if (btn.icon === 'circleX') {
        button.textContent = '비활성화'
      } else if (btn.icon === 'circleCheckBig') {
        button.textContent = '활성화'
      } else {
        button.textContent = btn.icon
      }
    }
    
    // 클릭 이벤트
    button.addEventListener('click', (e) => {
      e.stopPropagation()
      btn.onClick(rowData)
    })
    
    container.appendChild(button)
  })
  
  return container
}

// Action Buttons 컬럼 정의
const actionButtonsColumn = computed<ColDef | null>(() => {
  if (!props.actionButtons || props.actionButtons.length === 0) {
    return null
  }
  
  return {
    colId: 'actionButtons',
    headerName: '작업',
    field: '_actions',
    width: 120,
    minWidth: 80,
    maxWidth: 200,
    lockPosition: 'left' as const,
    sortable: false,
    filter: false,
    resizable: true,
    suppressNavigable: true,
    suppressHeaderMenuButton: true,
    cellRenderer: ActionButtonsCellRenderer,
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
  }
})

// checkbox 활성화 및 추가 컬럼 처리
const processedColumnDefs = computed(() => {
  const columns: ColDef[] = []
  
  // 1. Checkbox 컬럼 (최우선)
  if (props.checkbox) {
    columns.push({
      colId: 'rowSelection',
      headerName: '',
      // field와 valueGetter 제거 - AG Grid 공식 방식
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      lockPosition: 'left' as const,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      resizable: false,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      // 체크박스 셀 클릭 시 토글 (checkbox 아이콘 클릭은 AG Grid 기본 처리)
      onCellClicked: (params: CellClickedEvent) => {
        const target = (params.event as MouseEvent)?.target as HTMLElement
        if (target && !target.closest('.ag-checkbox-input-wrapper')) {
          params.node.setSelected(!params.node.isSelected())
        }
      },
    })
  }
  
  // 2. Action Buttons 컬럼
  if (actionButtonsColumn.value) {
    columns.push(actionButtonsColumn.value)
  }
  
  // 3. 사용자 정의 컬럼들 (Row Numbers는 AG Grid가 자동으로 추가)
  columns.push(...props.columnDefs)
  
  return columns
})

// Grid 옵션 설정
const gridOptions = computed<GridOptions>(() => {
  const useFloatingFilters = props.floatingFilter ?? props.floatingFilters
  // rowDragManaged 와 pagination 은 AG Grid 런타임에서 충돌 경고(및 managed drag 무효화)를 발생시키므로,
  // managed drag 가 켜지면 pagination 은 강제로 비활성화한다.
  const effectivePagination = props.rowDragManaged ? false : props.pagination
  if (props.rowDragManaged && props.pagination) {
    console.warn('[JGrid] rowDragManaged=true 이므로 pagination 은 자동으로 false 로 강제됩니다.')
  }
  const options: GridOptions = {
    theme: jTheme,
    pagination: effectivePagination,
    tooltipShowDelay: 500,
    tooltipHideDelay: 2000,
    overlayNoRowsTemplate: '<span class="j-grid-no-rows">데이터가 없습니다</span>',
    defaultColDef: {
      filter: true,
      floatingFilter: useFloatingFilters,
    },
    // AG Grid v32.2+ 권장 오브젝트 형태로 rowSelection 구성
    //   - 'multiple' (string) 및 suppressRowClickSelection 은 v32.2부터 deprecated
    //   - 체크박스만으로 선택을 제어하려면 enableClickSelection: false
    rowSelection: props.checkbox
      ? { mode: 'multiRow', enableClickSelection: false }
      : undefined,

    // Row Numbers (Enterprise) - AG Grid 표준 방식
    rowNumbers: props.rowNumbers
      ? ({
          minWidth: props.rowNumberWidth,
          width: props.rowNumberWidth,
        } as any)
      : false,

    // Column Hover Highlight
    columnHoverHighlight: props.columnHover,

    // Enterprise 기능 옵션
    sideBar: props.enableColumnsToolPanel || props.enableGrouping || props.enablePivot ? {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: !props.enableGrouping,
            suppressValues: !props.enablePivot,
            suppressPivots: !props.enablePivot,
            suppressPivotMode: !props.enablePivot,
          },
        },
      ],
      defaultToolPanel: '', // 초기에는 접힌 상태
    } : undefined,

    // Status Bar (Enterprise)
    // compactFooter 모드에서는 Status Bar 비활성화
    statusBar: (props.statusBar && !props.compactFooter) ? {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' as const },
        { statusPanel: 'agSelectedRowCountComponent', align: 'left' as const },
        { statusPanel: 'agAggregationComponent', align: 'right' as const },
      ],
    } : undefined,

    // Row Group Panel 설정 (Enterprise) - 그리드 상단에 드래그 영역 표시
    rowGroupPanelShow: props.rowGroupPanelShow !== 'never' ? props.rowGroupPanelShow : undefined,

    // Pivot Panel 설정 (Enterprise) - 피벗 모드용 드래그 영역
    pivotPanelShow: props.pivotPanelShow !== 'never' ? props.pivotPanelShow : undefined,
    
    // Pivot Mode 활성화
    pivotMode: props.pivotMode,

    // 그룹핑 기본 설정
    groupDefaultExpanded: props.groupDefaultExpanded,
    suppressAggFuncInHeader: false,

    // Tree Data 설정 (Enterprise)
    treeData: props.enableTreeData || undefined,
    getDataPath: props.enableTreeData
      ? (props.getDataPath || ((data: any) => data.path || []))
      : undefined,
    autoGroupColumnDef: props.enableTreeData && props.autoGroupColumnDef
      ? props.autoGroupColumnDef
      : undefined,

    // Row Drag 설정 (기본값 false 이므로 전달하지 않음 → AG Grid 기본 동작 유지)
    rowDragManaged: props.rowDragManaged || undefined,
    rowDragEntireRow: props.rowDragEntireRow || undefined,
    rowDragMultiRow: props.rowDragMultiRow || undefined,
  }

  return options
})

// Excel 내보내기 함수 (외부에서 사용 가능하도록 expose)
const exportToExcel = () => {
  if (gridApi.value && props.enableExcelExport) {
    gridApi.value.exportDataAsExcel({
      fileName: 'grid-export.xlsx',
    })
  }
}

// 그리드 API를 외부에 노출
defineExpose({
  gridApi,
  gridColumnApi,
  exportToExcel,
})

// Grid ready 이벤트 핸들러
const onGridReady = (params: GridReadyEvent) => {
  gridApi.value = params.api
  gridColumnApi.value = params.api // v34에서 columnApi는 deprecated
  emit('gridReady', params)
}

// 행 클릭 이벤트 핸들러
const onRowClicked = (event: RowClickedEvent) => {
  emit('rowClicked', event)
}

// 행 더블클릭 이벤트 핸들러
const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
  emit('rowDoubleClicked', event)
}

// 셀 클릭 이벤트 핸들러
const onCellClicked = (event: CellClickedEvent) => {
  emit('cellClicked', event)
}

// 선택 변경 이벤트 핸들러
const onSelectionChanged = (event: SelectionChangedEvent) => {
  emit('selectionChanged', event)
  emit('update:selectedRows', event.api.getSelectedRows())
}

// 셀 값 변경 이벤트 핸들러
const onCellValueChanged = (event: CellValueChangedEvent) => {
  emit('cellValueChanged', event)
}

// columnDefs 변경 감지
watch(
  () => props.columnDefs,
  () => {
    if (gridApi.value) {
      gridApi.value.setGridOption('columnDefs', processedColumnDefs.value)
    }
  },
  { deep: true },
)

// rowData 변경 감지
// reactiveRowData=true(기본): 내부 필드 변이까지 감지하여 AG Grid 에 rowData 재주입(기존 동작)
// reactiveRowData=false      : 배열 참조 교체 시에만 재주입. managed drag 와 함께 사용 시 드래그 중 롤백 방지.
// 주의: deep 옵션은 watcher 생성 시점에 고정되므로 런타임 토글은 지원하지 않음.
watch(
  () => props.rowData,
  () => {
    if (gridApi.value) {
      gridApi.value.setGridOption('rowData', props.rowData)
    }
  },
  { deep: props.reactiveRowData },
)
</script>

<template>
  <div ref="gridContainerRef" :class="cn('ag-grid-container', props.class)">
    <AgGridVue
      :column-defs="processedColumnDefs"
      :row-data="rowData"
      :grid-options="gridOptions"
      style="height: 100%; width: 100%"
      @grid-ready="onGridReady"
      @row-clicked="onRowClicked"
      @row-double-clicked="onRowDoubleClicked"
      @cell-clicked="onCellClicked"
      @selection-changed="onSelectionChanged"
      @cell-value-changed="onCellValueChanged"
    />
  </div>
</template>

<style scoped>
.ag-grid-container {
  width: 100%;
  height: 100%;
  min-height: 200px;

  /*
   * App CSS 변수 → AG Grid CSS 변수 브릿지
   * CSS 변수는 런타임 해소 → .dark 클래스 전환 시 라이트/다크 자동 대응
   * AG Grid v33 공식 CSS variable 명칭 사용
   */
  --ag-background-color:              hsl(var(--background));
  --ag-foreground-color:              hsl(var(--foreground));
  --ag-border-color:                  hsl(var(--border));
  --ag-header-background-color:       hsl(var(--muted));
  --ag-header-foreground-color:       hsl(var(--foreground));
  --ag-odd-row-background-color:      hsl(var(--card));
  --ag-row-hover-color:               hsl(var(--accent));
  --ag-selected-row-background-color: hsl(var(--primary) / 0.18);
  --ag-accent-color:                  hsl(var(--primary));
  --ag-header-height:                 var(--j-grid-header-h);
  --ag-row-height:                    var(--j-grid-row-h);
}

/* ============================================
   COMPACT FOOTER: Status Bar + Pagination 통합
   ============================================ */

/* Status Bar 높이 줄이기 */
:deep(.ag-status-bar) {
  min-height: var(--j-grid-footer-h) !important;
  height: var(--j-grid-footer-h) !important;
  padding: 0 8px !important;
  border-top: 1px solid var(--ag-border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.6875rem;
}

/* Status Bar 컴포넌트들 높이 조정 */
:deep(.ag-status-bar-left),
:deep(.ag-status-bar-center),
:deep(.ag-status-bar-right) {
  height: var(--j-grid-footer-h);
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Status Bar 패널들 간격 조정 */
:deep(.ag-status-panel) {
  padding: 0;
  height: var(--j-grid-footer-h);
  display: flex;
  align-items: center;
}

/* Pagination Panel 정렬 */
:deep(.ag-paging-panel) {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 15px !important;
  height: auto !important;
  padding: 4px 10px !important;
  border-top: 1px solid var(--ag-border-color);
  font-size: 0.6875rem;
}

/* Page Size 영역 정렬 */
:deep(.ag-paging-page-size) {
  display: inline-flex !important;
  align-items: center !important;
  margin: 0 !important;
}

:deep(.ag-paging-page-size .ag-picker-field-wrapper) {
  height: 24px !important;
  min-height: 24px !important;
  display: inline-flex !important;
  align-items: center !important;
  margin: 0 5px !important;
}

:deep(.ag-paging-page-size .ag-label) {
  margin: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
}

/* Row Summary 정렬 */
:deep(.ag-paging-row-summary-panel) {
  display: inline-flex !important;
  align-items: center !important;
  margin: 0 !important;
}

/* Page Summary (버튼 그룹) 정렬 */
:deep(.ag-paging-page-summary-panel) {
  display: inline-flex !important;
  align-items: center !important;
  margin: 0 !important;
  gap: 5px !important;
}

/* Pagination 버튼 높이 통일 */
:deep(.ag-paging-page-summary-panel .ag-paging-button) {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 24px !important;
  margin: 0 !important;
}

/* ========================================
   패턴 9: AG-Grid 스타일 향상
   ======================================== */

:deep(.ag-root-wrapper) {
  border: 1px solid hsl(var(--border));
  border-radius: 0.375rem;
}

/* ── Header ──────────────────────────────────────────────────────────── */
:deep(.ag-header) {
  border-bottom: 1px solid hsl(var(--border));
  font-weight: 600;
}

:deep(.ag-header-row:not(.ag-floating-filter)) {
  min-height: var(--j-grid-header-h) !important;
  height: var(--j-grid-header-h) !important;
}

:deep(.ag-header-cell) {
  color: hsl(var(--foreground));
  font-size: 0.75rem;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
}

/* Floating Filter: 헤더와 명확히 구분되는 입력 영역 */
:deep(.ag-header-row.ag-floating-filter) {
  min-height: var(--j-grid-filter-h) !important;
  height: var(--j-grid-filter-h) !important;
  background-color: hsl(var(--card));
  border-top: 1px solid hsl(var(--border) / 0.5);
}

:deep(.ag-floating-filter-body) {
  min-height: var(--j-grid-filter-h);
}

:deep(.ag-floating-filter-body input),
:deep(.ag-floating-filter-body .ag-input-field-input),
:deep(.ag-floating-filter-body .ag-picker-field-wrapper) {
  height: 20px !important;
  min-height: 20px !important;
  font-size: 0.75rem;
  background-color: hsl(var(--background)) !important;
  border: 1px solid hsl(var(--border)) !important;
  border-radius: 3px !important;
  padding: 0 4px !important;
  color: hsl(var(--foreground)) !important;
}

:deep(.ag-floating-filter-body input:focus),
:deep(.ag-floating-filter-body .ag-input-field-input:focus) {
  border-color: hsl(var(--primary)) !important;
  outline: none !important;
}

/* ── Rows ────────────────────────────────────────────────────────────── */
:deep(.ag-row) {
  min-height: var(--j-grid-row-h) !important;
  height: var(--j-grid-row-h) !important;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;
}

/* 선택된 행: 왼쪽 accent 기둥으로 명확한 시각적 선택 표시 (라이트/다크 모두) */
:deep(.ag-row-selected) {
  box-shadow: inset 4px 0 0 hsl(var(--primary));
}

/* ── Cells ───────────────────────────────────────────────────────────── */
:deep(.ag-cell) {
  line-height: 1.2;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  border-bottom: 1px solid hsl(var(--border) / 0.5);
}

:deep(.ag-cell-wrapper) {
  align-items: center;
}

:deep(.ag-cell-value) {
  display: flex;
  align-items: center;
  min-height: 100%;
}

/* 행 클릭 시 row number 셀에 보이는 강한 포커스 음영 제거 */
:deep(.ag-cell.ag-row-number-cell.ag-cell-focus),
:deep(.ag-cell.ag-row-number-cell:focus-within) {
  box-shadow: none !important;
  outline: none !important;
}

/* 셀 포커스 */
:deep(.ag-cell.ag-cell-focus) {
  box-shadow: inset 0 0 0 1px hsl(var(--ring) / 0.5) !important;
}

/* 인라인 편집 셀 */
:deep(.ag-cell-inline-editing) {
  padding: 0 !important;
  border: none !important;
  box-shadow: inset 0 0 0 1px hsl(var(--primary)) !important;
  background-color: hsl(var(--background)) !important;
}

:deep(.ag-cell-inline-editing .ag-cell-editor),
:deep(.ag-cell-inline-editing .ag-text-field-input-wrapper),
:deep(.ag-cell-inline-editing .ag-input-field) {
  height: 100% !important;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.ag-cell-inline-editing input) {
  height: 100% !important;
  width: 100% !important;
  padding: 0 0.5rem !important;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 0.75rem !important;
  color: hsl(var(--foreground)) !important;
}

/* ── Checkbox 셀 중앙정렬 (col-id 속성 선택자 — 신뢰할 수 있는 접근) ── */
:deep(.ag-cell[col-id="rowSelection"]) {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
}
:deep(.ag-cell[col-id="rowSelection"] .ag-cell-wrapper) {
  width: auto !important;
  flex: none !important;
}
:deep(.ag-header-cell[col-id="rowSelection"] .ag-header-cell-comp-wrapper) {
  justify-content: center !important;
}

/* ── Action Buttons (JS 렌더러 — CSS variable 브릿지) ─────────────────── */
:deep(.j-action-btn) {
  padding: 1px 8px;
  font-size: 0.75rem;
  border: 1px solid hsl(var(--border));
  border-radius: 3px;
  background-color: hsl(var(--card));
  color: hsl(var(--foreground));
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

:deep(.j-action-btn:hover) {
  background-color: hsl(var(--accent));
  border-color: hsl(var(--primary) / 0.3);
}

:deep(.j-action-btn-danger) {
  border-color: hsl(var(--destructive) / 0.4);
  background-color: hsl(var(--destructive) / 0.06);
  color: hsl(var(--destructive));
}

:deep(.j-action-btn-danger:hover) {
  background-color: hsl(var(--destructive) / 0.15);
}

/* ── No-Rows 오버레이 (한글) ──────────────────────────────────────── */
:deep(.j-grid-no-rows) {
  color: hsl(var(--muted-foreground));
  font-size: 0.75rem;
}
</style>
